import {
    getDeviationApprovedEmailTemplate,
    getDeviationNeedsApprovalEmailTemplate,
    getDeviationRejectedEmailTemplate,
    getMarketPriceApprovedEmailTemplate,
    getMarketPriceNeedsApprovalEmailTemplate,
    getMarketPriceRejectedEmailTemplate,
    getTierChangeApprovedEmailTemplate,
    getTierChangeRejectedEmailTemplate,
    getTierChangeRequestEmailTemplate,
} from 'shared/utility';
import {
    ApprovalTierChangeEmailData,
    ApprovedEmailData,
    NeedsApprovalEmailData,
    NeedsApprovalTierChangeEmailData,
    RejectionEmailData,
} from 'types/backend';
import { ICustomEventService } from './custom-event-service';
import { DynamicEmailData, EVENT_NAMES, IEmailAddressService } from './email-address-service';

export interface INotificationService {
    sendDeviationNeedsApprovalEmail(
        emailData: NeedsApprovalEmailData[],
        url: string,
        dynamicEmailData: DynamicEmailData
    ): Promise<void>;
    sendMarketPriceNeedsApprovalEmail(
        emailData: NeedsApprovalEmailData,
        url: string,
        dynamicEmailData: DynamicEmailData
    ): Promise<void>;
    // sendSimpleNeedsApprovalEmail(
    //     emailData: SimpleEmailData[],
    //     url: string,
    //     dynamicEmailData: DynamicEmailData
    // ): Promise<void>;
    sendRejectedDeviationEmail(
        emailData: RejectionEmailData,
        url: string,
        dynamicEmailData: DynamicEmailData
    ): Promise<void>;
    sendRejectedMarketPriceEmail(
        emailData: RejectionEmailData,
        url: string,
        dynamicEmailData: DynamicEmailData
    ): Promise<void>;
    sendApprovedMarketPriceEmail(
        emailData: ApprovedEmailData,
        url: string,
        dynamicEmailData: DynamicEmailData
    ): Promise<void>;
    sendApprovedDeviationEmail(
        emailData: ApprovedEmailData,
        url: string,
        dynamicEmailData: DynamicEmailData
    ): Promise<void>;
    sendTierchangeRequestEmail(
        emailData: NeedsApprovalTierChangeEmailData,
        url: string,
        dynamicEmailData: DynamicEmailData
    ): Promise<void>;
    sendTierchangeApprovedEmail(
        emailData: NeedsApprovalTierChangeEmailData,
        url: string,
        dynamicEmailData: DynamicEmailData
    ): Promise<void>;
    sendTierchangeRejectedEmail(
        emailData: NeedsApprovalTierChangeEmailData,
        url: string,
        dynamicEmailData: DynamicEmailData
    ): Promise<void>;
}

export class NotificationService implements INotificationService {
    constructor(private customEventService: ICustomEventService, private emailAddressService: IEmailAddressService) {}

    async sendDeviationNeedsApprovalEmail(
        emailData: NeedsApprovalEmailData[],
        url: string,
        dynamicEmailData: DynamicEmailData
    ): Promise<void> {
        const level1EmailData = emailData.filter((emailDataItem) => emailDataItem.approverLevel === 1);
        const level2EmailData = emailData.filter((emailDataItem) => emailDataItem.approverLevel === 2);

        if (level1EmailData.length > 0) {
            const toCCArrays = await this.emailAddressService.getToCCArrays(
                EVENT_NAMES.deviationRequestedLevel1,
                dynamicEmailData
            );

            level1EmailData.forEach(async (email) => {
                const emailHolder = getDeviationNeedsApprovalEmailTemplate(email, url);
                emailHolder.to = toCCArrays.to;
                emailHolder.cc = toCCArrays.cc;
                await this.customEventService.sendSimpleEmail(emailHolder);
            });
        }

        if (level2EmailData.length > 0) {
            const toCCArrays = await this.emailAddressService.getToCCArrays(
                EVENT_NAMES.deviationRequestedLevel2,
                dynamicEmailData
            );

            level2EmailData.forEach(async (email) => {
                const emailHolder = getDeviationNeedsApprovalEmailTemplate(email, url);
                emailHolder.to = toCCArrays.to;
                emailHolder.cc = toCCArrays.cc;
                await this.customEventService.sendSimpleEmail(emailHolder);
            });
        }
    }

    async sendMarketPriceNeedsApprovalEmail(
        emailData: NeedsApprovalEmailData,
        url: string,
        dynamicEmailData: DynamicEmailData
    ): Promise<void> {
        const toCCArrays = await this.emailAddressService.getToCCArrays(
            emailData.approverLevel === 1
                ? EVENT_NAMES.marketPriceRequestedLevel1
                : EVENT_NAMES.marketPriceRequestedLevel2,
            dynamicEmailData
        );

        const emailHolder = getMarketPriceNeedsApprovalEmailTemplate(emailData, url);
        emailHolder.to = toCCArrays.to;
        emailHolder.cc = toCCArrays.cc;
        await this.customEventService.sendSimpleEmail(emailHolder);
    }

    // async sendSimpleNeedsApprovalEmail(emailData: SimpleEmailData[], url: string): Promise<void> {
    //     emailData.forEach(async (email) => {
    //         let emailHolder = getSimpleNeedsApprovalEmailTemplate(email, url);
    //         await this.customEventService.sendSimpleEmail(emailHolder);
    //     });
    // }

    async sendRejectedDeviationEmail(
        emailData: RejectionEmailData,
        url: string,
        dynamicEmailData: DynamicEmailData
    ): Promise<void> {
        const toCCArrays = await this.emailAddressService.getToCCArrays(
            EVENT_NAMES.deviationRejected,
            dynamicEmailData
        );

        const emailHolder = getDeviationRejectedEmailTemplate(emailData, url);
        emailHolder.cc = toCCArrays.cc;

        await this.customEventService.sendSimpleEmail(emailHolder);
    }

    async sendRejectedMarketPriceEmail(
        emailData: RejectionEmailData,
        url: string,
        dynamicEmailData: DynamicEmailData
    ): Promise<void> {
        const toCCArrays = await this.emailAddressService.getToCCArrays(
            EVENT_NAMES.marketPriceRejected,
            dynamicEmailData
        );

        const emailHolder = getMarketPriceRejectedEmailTemplate(emailData, url);
        emailHolder.cc = toCCArrays.cc;

        await this.customEventService.sendSimpleEmail(emailHolder);
    }

    async sendApprovedDeviationEmail(
        emailData: ApprovedEmailData,
        url: string,
        dynamicEmailData: DynamicEmailData
    ): Promise<void> {
        const toCCArrays = await this.emailAddressService.getToCCArrays(
            EVENT_NAMES.deviationApproved,
            dynamicEmailData
        );

        let emailHolder = getDeviationApprovedEmailTemplate(emailData, url);
        emailHolder.cc = toCCArrays.cc;
        await this.customEventService.sendSimpleEmail(emailHolder);
    }

    async sendApprovedMarketPriceEmail(
        emailData: ApprovedEmailData,
        url: string,
        dynamicEmailData: DynamicEmailData
    ): Promise<void> {
        const toCCArrays = await this.emailAddressService.getToCCArrays(
            EVENT_NAMES.marketPriceApproved,
            dynamicEmailData
        );

        let emailHolder = getMarketPriceApprovedEmailTemplate(emailData, url);
        emailHolder.cc = toCCArrays.cc;

        await this.customEventService.sendSimpleEmail(emailHolder);
    }

    async sendTierchangeRequestEmail(
        emailData: NeedsApprovalTierChangeEmailData,
        url: string,
        dynamicEmailData: DynamicEmailData
    ): Promise<void> {
        const toCCArrays = await this.emailAddressService.getToCCArrays(
            EVENT_NAMES.tierChangeRequested,
            dynamicEmailData
        );

        let emailHolder = getTierChangeRequestEmailTemplate(emailData, url);
        emailHolder.to = toCCArrays.to;
        emailHolder.cc = toCCArrays.cc;

        await this.customEventService.sendSimpleEmail(emailHolder);
    }

    async sendTierchangeRejectedEmail(
        emailData: ApprovalTierChangeEmailData,
        url: string,
        dynamicEmailData: DynamicEmailData
    ): Promise<void> {
        const toCCArrays = await this.emailAddressService.getToCCArrays(
            EVENT_NAMES.tierChangeRejected,
            dynamicEmailData
        );

        let emailHolder = getTierChangeRejectedEmailTemplate(emailData, url);
        emailHolder.cc = toCCArrays.cc;

        await this.customEventService.sendSimpleEmail(emailHolder);
    }

    async sendTierchangeApprovedEmail(
        emailData: ApprovalTierChangeEmailData,
        url: string,
        dynamicEmailData: DynamicEmailData
    ): Promise<void> {
        const toCCArrays = await this.emailAddressService.getToCCArrays(
            EVENT_NAMES.tierChangeApproved,
            dynamicEmailData
        );

        let emailHolder = getTierChangeApprovedEmailTemplate(emailData, url);
        emailHolder.cc = toCCArrays.cc;

        await this.customEventService.sendSimpleEmail(emailHolder);
    }
}
